import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { CANCER_TYPE_COMBOBOX_TEXT } from '@/constants/cancerTypeCombobox';
import {
  trackCommunityCancerTypeSelected,
  trackCommunityCancerTypesViewed,
} from '@/util/analyticsEvents';

type CommunityCancerTypeFilterProps = {
  /** Selected cancer type id, 0 for none. */
  cancerTypeId: number;
  onChange: (cancerTypeId: number) => void;
  options: ComboboxOption[];
  /** The selection is reported but not applied while the reader still has to verify. */
  needsNpiVerification?: boolean;
  textColor?: string;
  bottomInset?: number;
  /** Extra classes for the web field's box, such as a height to match its neighbors. */
  fieldClassName?: string;
  /** The options are still loading, so the picker does not claim there are none. */
  isLoading?: boolean;
};

/** Inquiry Library cancer type filter: the shared picker, grouped, plus the library's analytics and gating. */
export function CommunityCancerTypeFilter({
  cancerTypeId,
  onChange,
  options,
  needsNpiVerification = false,
  textColor,
  bottomInset,
  fieldClassName,
  isLoading = false,
}: CommunityCancerTypeFilterProps) {
  return (
    <Combobox
      {...CANCER_TYPE_COMBOBOX_TEXT}
      className="mb-0 min-h-[44px] w-full"
      value={cancerTypeId ? String(cancerTypeId) : ''}
      options={options}
      grouped
      hideEmpty
      // The counts come from Postgres and the list from the search index, so they can disagree
      // (31 shown, 1 returned). They still hide empty types, but are not displayed.
      showCounts={false}
      isLoading={isLoading}
      fieldClassName={fieldClassName}
      showSearchIcon={false}
      placeholder="Filter by cancer type"
      label="Filter by cancer type"
      clearLabel="Clear cancer type filter"
      textColor={textColor}
      bottomInset={bottomInset}
      analytics={{
        onViewed: () => {
          trackCommunityCancerTypesViewed();
        },
        onSelected: (id) => {
          trackCommunityCancerTypeSelected({
            appliedCancerTypeId: Number(id),
            label: options.find((option) => option.value === id)?.label || '',
          });
        },
      }}
      onChange={(id) => {
        if (needsNpiVerification) {
          return;
        }

        onChange(Number(id));
      }}
    />
  );
}
